import { useEffect, useState } from 'react';
import { useTheme } from '@/lib/theme';

const SECTIONS = [
  { href: '#squeeze', label: 'Demo' },
  { href: '#anatomy', label: 'Anatomy' },
  { href: '#trail', label: 'Trail' },
];

/**
 * Nav — minimal, sticky, glass only when scrolled.
 *
 * The Open section already shows the wordmark + version + GitHub link, so
 * the nav doesn't repeat itself. Above the fold the nav is invisible
 * (no border, transparent bg). Once scrolled, it fades into a thin
 * tinted bar.
 */
export function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const { theme, toggle } = useTheme();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <nav
      className="fixed inset-x-0 top-0 z-[1000]"
      style={{
        background: scrolled
          ? 'color-mix(in oklch, var(--bg) 86%, transparent)'
          : 'transparent',
        backdropFilter: scrolled ? 'blur(18px) saturate(140%)' : 'none',
        WebkitBackdropFilter: scrolled ? 'blur(18px) saturate(140%)' : 'none',
        borderBottom: scrolled ? '1px solid var(--border)' : '1px solid transparent',
        transition:
          'background var(--dur-base) var(--ease-out-expo), border-color var(--dur-base) var(--ease-out-expo), backdrop-filter var(--dur-base) var(--ease-out-expo)',
        opacity: scrolled ? 1 : 0,
        pointerEvents: scrolled ? 'auto' : 'none',
      }}
    >
      <div
        className="container-x flex items-center justify-between"
        style={{ height: 56 }}
      >
        <a
          href="#open"
          className="t-mono"
          style={{
            fontSize: 'var(--type-sm)',
            fontWeight: 500,
            letterSpacing: 'var(--tracking-tight)',
          }}
        >
          slobozip
          <span style={{ color: 'var(--text-3)', marginLeft: 8 }}>v0.3.0</span>
        </a>

        <div className="flex items-center" style={{ gap: 4 }}>
          <ul className="hidden md:flex items-center" style={{ gap: 4, listStyle: 'none' }}>
            {SECTIONS.map((s) => (
              <li key={s.href}>
                <a
                  href={s.href}
                  className="t-mono"
                  style={{
                    fontSize: 'var(--type-xs)',
                    color: 'var(--text-2)',
                    padding: '8px 14px',
                    letterSpacing: 'var(--tracking-mono-caps)',
                    textTransform: 'uppercase',
                    transition: 'color var(--dur-fast) var(--ease-out-expo)',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--text)')}
                  onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--text-2)')}
                >
                  {s.label}
                </a>
              </li>
            ))}
          </ul>

          <button
            onClick={toggle}
            aria-label={`Switch to ${theme === 'light' ? 'dark' : 'light'} theme`}
            style={{
              width: 32,
              height: 32,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--text-2)',
              transition: 'color var(--dur-fast) var(--ease-out-expo)',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--text)')}
            onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--text-2)')}
          >
            {theme === 'light' ? <MoonIcon /> : <SunIcon />}
          </button>
        </div>
      </div>
    </nav>
  );
}

function MoonIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
    </svg>
  );
}

function SunIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
    </svg>
  );
}
